import { GroundType } from 'models/ground';
import React, { useMemo } from 'react';
import { Group, Line } from 'react-konva';
import {
  DEFAULT_PITCH_LINE_COLOR,
  DEFAULT_PITCH_STROKEWIDTH,
  RUGBY_10,
  RUGBY_15,
  RUGBY_22,
  RUGBY_5,
  RUGBY_DASH,
  RUGBY_MINIMUM_WIDTH,
} from '../constants';
import Vertices from '../Vertices';
import { GroundGeometry } from '../../../../types/webPlayer';

function Area(props: {
  vertexA: { x: number; y: number };
  vertexC: { x: number; y: number };
  height: number;
  width: number;
  center: { x: number; y: number };
  scale: number;
  flipped?: boolean;
}) {
  const {
    vertexA, vertexC, height, width, scale, flipped, center,
  } = props;

  const scaledMinimumWidth = RUGBY_MINIMUM_WIDTH * scale;
  const scaled22LineOffset = RUGBY_22 * scale;
  const scaled15LineOffset = RUGBY_15 * scale;
  const scaled10LineOffset = RUGBY_10 * scale;
  const scaled5LineOffset = RUGBY_5 * scale;
  const scaledDash = RUGBY_DASH * scale;

  const isReversed = vertexA.y < vertexC.y;

  //  scaledDash / 2
  const geometry = useMemo(() => ({
    col1: flipped
      ? vertexC.x - scaledDash - (width - scaledMinimumWidth) / 2 - scaledDash / 2
      : vertexA.x + scaledDash + (width - scaledMinimumWidth) / 2 + scaledDash / 2,
    col2: flipped
      ? vertexC.x - scaled22LineOffset - (width - scaledMinimumWidth) / 2
      : vertexA.x + scaled22LineOffset + (width - scaledMinimumWidth) / 2,
    col3: flipped
      ? vertexC.x + scaled10LineOffset - width / 2
      : vertexA.x - scaled10LineOffset + width / 2,
    col1Reverse: flipped ? -scaledDash / 2 : scaledDash / 2,
    row1: isReversed ? vertexC.y - height + scaledDash : vertexA.y - height + scaledDash,
    row2: isReversed ? vertexC.y - height + scaled15LineOffset : vertexA.y - height + scaled15LineOffset,
    row3: isReversed ? vertexC.y - scaled15LineOffset : vertexA.y - scaled15LineOffset,
    row4: isReversed ? vertexC.y - scaled5LineOffset : vertexA.y - scaled5LineOffset,
    dash1: {
      col: flipped
        ? vertexC.x - scaledDash - (width - scaledMinimumWidth) / 2
        : vertexA.x + scaledDash + (width - scaledMinimumWidth) / 2,
      start: center.y - scaledDash * 1.5,
      end: center.y + scaledDash * 1.5,
    },
    dash2: {
      col: flipped
        ? vertexC.x + scaled10LineOffset - width / 2
        : vertexA.x - scaled10LineOffset + width / 2,
      start: center.y - scaledDash * 2.5,
      end: center.y + scaledDash * 2.5,
    },
    line1: {
      col: flipped
        ? vertexC.x - (width - scaledMinimumWidth) / 2
        : vertexA.x + (width - scaledMinimumWidth) / 2,
      start: vertexA.y,
      end: vertexC.y,
    },
    line2: {
      col: flipped
        ? vertexC.x - scaled22LineOffset - (width - scaledMinimumWidth) / 2
        : vertexA.x + scaled22LineOffset + (width - scaledMinimumWidth) / 2,
      start: vertexA.y,
      end: vertexC.y,
    },
  }), [vertexA, vertexC, height, width, scale, flipped, center]);

  const lines = useMemo(() => ({
    line1_1: [
      geometry.col1 - scaledDash / 2, geometry.row1,
      geometry.col1 + scaledDash / 2, geometry.row1,
    ],
    line1_1_dash: [
      geometry.col1 - geometry.col1Reverse, geometry.row1 - scaledDash / 2,
      geometry.col1 - geometry.col1Reverse, geometry.row1 + scaledDash / 2,
    ],
    line1_2: [
      geometry.col2 - scaledDash / 2, geometry.row1,
      geometry.col2 + scaledDash / 2, geometry.row1,
    ],
    line1_2_dash: [
      geometry.col2, geometry.row1 - scaledDash / 2,
      geometry.col2, geometry.row1 + scaledDash / 2,
    ],
    line1_3: [
      geometry.col3 - scaledDash / 2, geometry.row1,
      geometry.col3 + scaledDash / 2, geometry.row1,
    ],
    line1_3_dash: [
      geometry.col3, geometry.row1 - scaledDash / 2,
      geometry.col3, geometry.row1 + scaledDash / 2,
    ],
    line2_1: [
      geometry.col1 - scaledDash / 2, geometry.row2,
      geometry.col1 + scaledDash / 2, geometry.row2,
    ],
    line2_1_dash: [
      geometry.col1 - geometry.col1Reverse, geometry.row2 - scaledDash / 2,
      geometry.col1 - geometry.col1Reverse, geometry.row2 + scaledDash / 2,
    ],
    line2_2: [
      geometry.col2 - scaledDash / 2, geometry.row2,
      geometry.col2 + scaledDash / 2, geometry.row2,
    ],
    line2_2_dash: [
      geometry.col2, geometry.row2 - scaledDash / 2,
      geometry.col2, geometry.row2 + scaledDash / 2,
    ],
    line2_3: [
      geometry.col3 - scaledDash / 2, geometry.row2,
      geometry.col3 + scaledDash / 2, geometry.row2,
    ],
    line2_3_dash: [
      geometry.col3, geometry.row2 - scaledDash / 2,
      geometry.col3, geometry.row2 + scaledDash / 2,
    ],
    line3_1: [
      geometry.col1 - scaledDash / 2,
      geometry.row3,
      geometry.col1 + scaledDash / 2,
      geometry.row3,
    ],
    line3_1_dash: [
      geometry.col1 - geometry.col1Reverse,
      geometry.row3 - scaledDash / 2,
      geometry.col1 - geometry.col1Reverse,
      geometry.row3 + scaledDash / 2,
    ],
    line3_2: [
      geometry.col2 - scaledDash / 2,
      geometry.row3,
      geometry.col2 + scaledDash / 2,
      geometry.row3,
    ],
    line3_2_dash: [
      geometry.col2,
      geometry.row3 - scaledDash / 2,
      geometry.col2,
      geometry.row3 + scaledDash / 2,
    ],
    line3_3: [
      geometry.col3 - scaledDash / 2,
      geometry.row3,
      geometry.col3 + scaledDash / 2,
      geometry.row3,
    ],
    line3_3_dash: [
      geometry.col3,
      geometry.row3 - scaledDash / 2,
      geometry.col3,
      geometry.row3 + scaledDash / 2,
    ],
    line4_1: [
      geometry.col1 - scaledDash / 2,
      geometry.row4,
      geometry.col1 + scaledDash / 2,
      geometry.row4,
    ],
    line4_1_dash: [
      geometry.col1 - geometry.col1Reverse,
      geometry.row4 - scaledDash / 2,
      geometry.col1 - geometry.col1Reverse,
      geometry.row4 + scaledDash / 2,
    ],
    line4_2: [
      geometry.col2 - scaledDash / 2,
      geometry.row4,
      geometry.col2 + scaledDash / 2,
      geometry.row4,
    ],
    line4_2_dash: [
      geometry.col2,
      geometry.row4 - scaledDash / 2,
      geometry.col2,
      geometry.row4 + scaledDash / 2,
    ],
    line4_3: [
      geometry.col3 - scaledDash / 2,
      geometry.row4,
      geometry.col3 + scaledDash / 2,
      geometry.row4,
    ],
    line4_3_dash: [
      geometry.col3,
      geometry.row4 - scaledDash / 2,
      geometry.col3,
      geometry.row4 + scaledDash / 2,
    ],
    line_center_1: [
      geometry.dash1.col,
      geometry.dash1.start,
      geometry.dash1.col,
      geometry.dash1.end,
    ],
    line_center_3: [
      geometry.dash2.col,
      geometry.dash2.start,
      geometry.dash2.col,
      geometry.dash2.end,
    ],
    line_1: [
      geometry.line1.col,
      geometry.line1.start,
      geometry.line1.col,
      geometry.line1.end,
    ],
    line_2: [
      geometry.line2.col,
      geometry.line2.start,
      geometry.line2.col,
      geometry.line2.end,
    ],
  }), [
    geometry.col1, geometry.row1, scaledDash, geometry.col1Reverse,
    geometry.col2, geometry.row2, geometry.col3, geometry.row3,
    geometry.col1, geometry.row4, geometry.dash1.col, geometry.dash1.start,
    geometry.dash1.end, geometry.dash2.col, geometry.dash2.start,
    geometry.dash2.end, geometry.line1.col, geometry.line1.start,
    geometry.line1.end, geometry.line2.col, geometry.line2.start, geometry.line2.end,
  ]);

  return (
    <Group>

      {/* T, riga 1, colonna 1 */}
      <Line points={lines.line1_1} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line1_1_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* X, riga 1, colonna 2 */}
      <Line points={lines.line1_2} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line1_2_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* X, riga 1, colonna 3 */}
      <Line points={lines.line1_3} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line1_3_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* T, riga 2, colonna 1 */}
      <Line points={lines.line2_1} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line2_1_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* X, riga 2, colonna 2 */}
      <Line points={lines.line2_2} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line2_2} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* X, riga 2, colonna 3 */}
      <Line points={lines.line2_3} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line2_3_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* T, riga 3, colonna 1 */}
      <Line points={lines.line3_1} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line3_1_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* X, riga 3, colonna 2 */}
      <Line points={lines.line3_2} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line3_2_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* X, riga 3, colonna 3 */}
      <Line points={lines.line3_3} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line3_3_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* T, riga 4, colonna 1 */}
      <Line points={lines.line4_1} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line4_1_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* X, riga 4, colonna 2 */}
      <Line points={lines.line4_2} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line4_2_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* X, riga 4, colonna 3 */}
      <Line points={lines.line4_3} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={lines.line4_3_dash} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* centro prima colonna */}
      <Line
        points={lines.line_center_1}
        dash={[scaledDash, scaledDash]}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />

      {/* centro terza colonna */}
      <Line
        points={lines.line_center_3}
        dash={[scaledDash, scaledDash]}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />

      {/* Prima linea */}
      <Line points={lines.line_1} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      {/* Seconda linea */}
      <Line points={lines.line_2} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
    </Group>
  );
}

export default function Rugby(props: {
  groundData: GroundType;
  scale: number;
  vertices: boolean;
  groundGeometry: GroundGeometry;
}) {
  const { groundData, vertices, groundGeometry } = props;

  const dataScale = props.scale / groundGeometry.scale;

  const {
    scaledDims, center, scale, vertex: vertexPoints,
  } = groundGeometry;
  const { height, width } = scaledDims;

  if (vertexPoints.vertexA.x > vertexPoints.vertexC.x) {
    const swap = vertexPoints.vertexC;
    vertexPoints.vertexC = vertexPoints.vertexA;
    vertexPoints.vertexA = swap;
  }

  const isReversed = vertexPoints.vertexA.y < vertexPoints.vertexC.y;

  const scaled15LineOffset = RUGBY_15 * scale;
  const scaled5LineOffset = RUGBY_5 * scale;
  const scaledDash = RUGBY_DASH * scale;

  const geometry = useMemo(() => ({
    center: {
      x0: vertexPoints.vertexA.x + width / 2,
      x1: center.x - scaledDash / 2,
      x2: center.x + scaledDash / 2,
      y1: isReversed
        ? center.y - scaled5LineOffset
        : center.y + scaled5LineOffset,
      y2: isReversed
        ? center.y - scaled15LineOffset
        : center.y + scaled15LineOffset,
      y3: isReversed
        ? center.y + scaled5LineOffset
        : center.y - scaled5LineOffset,
      y4: isReversed
        ? center.y + scaled15LineOffset
        : center.y - scaled15LineOffset,
    },
    line: {
      start: vertexPoints.vertexA.x + width / 2,
      end: vertexPoints.vertexC.x - width / 2,
    },
  }), [
    vertexPoints.vertexA, vertexPoints.vertexC,
    width, scaledDash, height, center,
    scaled5LineOffset, scaled15LineOffset,
  ]);

  return (
    <Group scaleX={dataScale} scaleY={dataScale}>
      {/* border */}
      <Line
        fill={groundData.groundSurfaceColor ? groundData.groundSurfaceColor : '#00000000'}
        points={[
          vertexPoints.vertexA.x, vertexPoints.vertexA.y,
          vertexPoints.vertexC.x, vertexPoints.vertexA.y,
          vertexPoints.vertexC.x, vertexPoints.vertexC.y,
          vertexPoints.vertexA.x, vertexPoints.vertexC.y,
        ]}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        closed
      />

      {/* left area */}
      <Area
        vertexA={vertexPoints.vertexA}
        vertexC={vertexPoints.vertexC}
        height={height}
        width={width}
        center={center}
        scale={scale}
      />

      {/* halfcourt */}
      <Line
        points={[
          geometry.line.start,
          vertexPoints.vertexA.y,
          geometry.line.end,
          vertexPoints.vertexC.y,
        ]}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Line
        points={[
          geometry.center.x1,
          geometry.center.y1,
          geometry.center.x2,
          geometry.center.y1,
        ]}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Line
        points={[
          geometry.center.x1,
          geometry.center.y2,
          geometry.center.x2,
          geometry.center.y2,
        ]}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Line
        points={[
          geometry.center.x1,
          geometry.center.y3,
          geometry.center.x2,
          geometry.center.y3,
        ]}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Line
        points={[
          geometry.center.x1,
          geometry.center.y4,
          geometry.center.x2,
          geometry.center.y4,
        ]}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />

      {/* right area */}
      <Area
        vertexA={vertexPoints.vertexA}
        vertexC={vertexPoints.vertexC}
        height={height}
        width={width}
        center={center}
        scale={scale}
        flipped
      />

      {vertices ? <Vertices A={vertexPoints.vertexA} C={vertexPoints.vertexC} /> : null}
    </Group>
  );
}
